import { useState } from 'react';
import { useHistory } from 'react-router-dom';
import { Button, Grid, Link, Typography } from '@mui/material';
import { makeStyles } from '@mui/styles';
import {
  DataGridPremium as DataGrid,
  GridColDef,
  GridRenderCellParams,
} from '@mui/x-data-grid-premium';
import {
  EstateResultRule,
  EstateResultRuleActionType,
  EstateResultRuleComparisonType,
  EstateResultRuleType,
  useEstateResultRule_GetAllEstateResultRulesQuery,
} from 'queries';
import { ChevronRight, Icon } from 'ts-react-feather-icons';

import Spinner from 'common/spinner';
import HomeLayout from 'layouts/home-layout';
import { NotificationSeverity, showAlert } from 'utils/notification-manager';

import CreateEstateResultRuleModal from './components/create-estate-result-rule-modal';
import UpdateEstateResultRuleActionModal from './components/update-estate-result-rule-action-modal';

const useStyles = makeStyles(() => ({
  root: {
    // no border around the cells when focused
    '&.MuiDataGrid-root .MuiDataGrid-cell, &.MuiDataGrid-root .MuiDataGrid-columnHeader': {
      '&:focus, &:focus-within': {
        outline: 'none',
      },
    },
  },
  container: {
    padding: '5%',
    flexGrow: 1,
  },
  title: {
    fontWeight: 600,
  },
  routeIconContainer: {
    verticalAlign: 'middle',
  },
  link: {
    cursor: 'pointer',
  },
  description: {
    marginRight: '10px',
  },
}));

export default function EstateRulesPage() {
  const classes = useStyles();
  const history = useHistory();

  const [currentEstateResultRule, setCurrentEstateResultRule] = useState<
    EstateResultRule | undefined
  >();
  const [showCreateEstateResultRuleModal, setShowCreateEstateResultRuleModal] = useState(false);
  const [showUpdateEstateResultRuleActionModal, setShowUpdateEstateResultRuleActionModal] =
    useState(false);

  const { data: estateResultRulesData, loading: estateResultRulesLoading } =
    useEstateResultRule_GetAllEstateResultRulesQuery({
      onError: (e) => {
        showAlert({
          message: 'Error getting all estate result rules: ' + e.message,
          severity: NotificationSeverity.ERROR,
        });
      },
    });

  if (estateResultRulesLoading) {
    return <Spinner />;
  }

  const getComparisonTypeDisplay = (comparisonType: EstateResultRuleComparisonType): string => {
    let comparisonTypeDisplay: string;
    switch (comparisonType) {
      case EstateResultRuleComparisonType.None:
        comparisonTypeDisplay = 'None';
        break;
      case EstateResultRuleComparisonType.Contains:
        comparisonTypeDisplay = 'Contains';
        break;
      case EstateResultRuleComparisonType.Equals:
        comparisonTypeDisplay = 'Equals';
        break;
      case EstateResultRuleComparisonType.StartsWith:
        comparisonTypeDisplay = 'Starts With';
        break;
      case EstateResultRuleComparisonType.EndsWith:
        comparisonTypeDisplay = 'Ends With';
        break;
      case EstateResultRuleComparisonType.NotContains:
        comparisonTypeDisplay = 'Not Contains';
        break;
      case EstateResultRuleComparisonType.NotEquals:
        comparisonTypeDisplay = 'Not Equals';
        break;
      case EstateResultRuleComparisonType.NotStartsWith:
        comparisonTypeDisplay = 'Not Starts With';
        break;
      case EstateResultRuleComparisonType.NotEndsWith:
        comparisonTypeDisplay = 'Not ends With';
        break;
      default:
        comparisonTypeDisplay = comparisonType;
        break;
    }

    return comparisonTypeDisplay;
  };

  const columns: GridColDef[] = [
    {
      disableColumnMenu: true,
      field: 'description',
      minWidth: 250,
      headerName: 'Description',
      renderCell: (params: GridRenderCellParams) => {
        const estateResultRule = params.row as EstateResultRule;

        return (
          <>
            <Link
              className={classes.link}
              onClick={() => {
                setCurrentEstateResultRule(estateResultRule);
                setShowCreateEstateResultRuleModal(true);
              }}
            >
              <Icon name="edit-2" size={16} color="#44D2F2" />
            </Link>
            &nbsp;
            <Typography className={classes.description}>{params.row.description}</Typography>
          </>
        );
      },
    },
    {
      disableColumnMenu: true,
      field: 'type',
      minWidth: 200,
      headerName: 'Type',
      renderCell: (params: GridRenderCellParams) => {
        const estateResultRule = params.row as EstateResultRule;

        let ruleType = estateResultRule.type.toString();
        switch (estateResultRule.type) {
          case EstateResultRuleType.SuspectedAccount:
            ruleType = 'Suspected Account';
            break;
        }

        return (
          <>
            <Typography className={classes.description}>{ruleType}</Typography>
          </>
        );
      },
    },
    {
      disableColumnMenu: true,
      field: 'action',
      minWidth: 200,
      headerName: 'Action',
      renderCell: (params: GridRenderCellParams) => {
        const estateResultRule = params.row as EstateResultRule;

        if (!estateResultRule.action) {
          return '*No Action*';
        }

        let actionType: string;
        switch (estateResultRule.action.type) {
          case EstateResultRuleActionType.None:
            actionType = 'None';
            break;
          case EstateResultRuleActionType.Log:
            actionType = 'Log';
            break;
          case EstateResultRuleActionType.NewResultItem:
            actionType = 'New Result Item';
            break;
          case EstateResultRuleActionType.ChangeTitle:
            actionType = 'Change Title';
            break;
          case EstateResultRuleActionType.ChangeSubtitle:
            actionType = 'Change Subtitle';
            break;
          default:
            actionType = estateResultRule.action.type.toString();
            break;
        }

        return (
          <>
            <Link
              className={classes.link}
              onClick={() => {
                setCurrentEstateResultRule(estateResultRule);
                setShowUpdateEstateResultRuleActionModal(true);
              }}
            >
              <Icon name="edit-2" size={16} color="#44D2F2" />
            </Link>
            &nbsp;
            <Typography className={classes.description}>{actionType}</Typography>
          </>
        );
      },
    },
    {
      disableColumnMenu: true,
      field: 'headerComparisonType',
      minWidth: 250,
      headerName: 'Header Filter',
      renderCell: (params: GridRenderCellParams) => {
        const estateResultRule = params.row as EstateResultRule;
        const comparisonType = getComparisonTypeDisplay(estateResultRule.headerComparisonType);

        return (
          <div>
            <Typography className={classes.description}>{comparisonType}</Typography>
            <Typography className={classes.description}>
              '{estateResultRule.headerComparisonValue}'
            </Typography>
          </div>
        );
      },
    },
    {
      disableColumnMenu: true,
      field: 'subheaderComparisonType',
      minWidth: 250,
      headerName: 'Subheader Filter',
      renderCell: (params: GridRenderCellParams) => {
        const estateResultRule = params.row as EstateResultRule;
        const comparisonType = getComparisonTypeDisplay(estateResultRule.subheaderComparisonType);

        return (
          <div>
            <Typography className={classes.description}>{comparisonType}</Typography>
            <Typography className={classes.description}>
              '{estateResultRule.subheaderComparisonValue}'
            </Typography>
          </div>
        );
      },
    },
    {
      disableColumnMenu: true,
      field: 'isActive',
      minWidth: 50,
      headerName: 'Active',
      renderCell: (params: GridRenderCellParams) => {
        const estateResultRule = params.row as EstateResultRule;

        return (
          <>
            <Typography className={classes.description}>
              {estateResultRule.isActive ? 'Yes' : 'No'}
            </Typography>
          </>
        );
      },
    },
  ];

  const rows = [];
  const estateRules = estateResultRulesData ? estateResultRulesData.data : [];
  for (const estateRule of estateRules) {
    rows.push(estateRule);
  }

  return (
    <HomeLayout showPortalName={false}>
      <>
        <CreateEstateResultRuleModal
          currentEstateResultRule={currentEstateResultRule}
          setCurrentEstateResultRule={setCurrentEstateResultRule}
          showModal={showCreateEstateResultRuleModal}
          setShowModal={setShowCreateEstateResultRuleModal}
        />
        {currentEstateResultRule && currentEstateResultRule.action && (
          <UpdateEstateResultRuleActionModal
            currentEstateResultRule={currentEstateResultRule}
            setCurrentEstateResultRule={setCurrentEstateResultRule}
            showModal={showUpdateEstateResultRuleActionModal}
            setShowModal={setShowUpdateEstateResultRuleActionModal}
          />
        )}
        <div className={classes.container}>
          <div style={{ display: 'flex', verticalAlign: 'bottom' }}>
            <Typography className={classes.title} variant="h5" style={{ flex: 1 }}>
              <span
                onClick={() => {
                  history.push(`/home`);
                }}
              >
                <span className={classes.routeIconContainer}>
                  <img src="/home-icon.svg" alt="home-icon" />
                </span>
                <span className={classes.routeIconContainer}>
                  <ChevronRight />
                </span>
              </span>
              Manage Estate Rules
            </Typography>
            <div style={{ flex: 3 }}>
              <Button
                variant="contained"
                color="primary"
                size="small"
                style={{ marginTop: '3px', padding: '2px 10px' }}
                onClick={() => {
                  setShowCreateEstateResultRuleModal(true);
                }}
              >
                + Add a New Estate Rule
              </Button>
            </div>
          </div>
          <Grid container spacing={2}>
            <Grid item md={12} xs={12}>
              <div style={{ width: '100%' }}>
                <DataGrid
                  className={classes.root}
                  columns={columns}
                  rows={rows}
                  disableColumnReorder
                  disableRowSelectionOnClick
                  autoHeight={true}
                  rowHeight={65}
                  pagination
                  initialState={{
                    pagination: { paginationModel: { pageSize: 100 } },
                  }}
                />
              </div>
            </Grid>
          </Grid>
        </div>
      </>
    </HomeLayout>
  );
}
